import {Injectable} from '@angular/core';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {BehaviorSubject, EMPTY, Observable, of, Subscription, switchMap} from 'rxjs';
import {
  IAllAnnotatedStatements, ICategories,
  IDictionnaries
} from '@app/components/projects/surveys/annotation/store/annotations.entitiy';
import {removeUndefined} from '@app/components/_common/firebase.utilities';
import {take} from 'rxjs/operators';
import {cloneDeep} from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class AnnotationsRealtimeService {

  surveyId: string;
  annoByUser$ = new BehaviorSubject<IAllAnnotatedStatements>({});
  dictionary$ = new BehaviorSubject<IDictionnaries>(null);
  categories$: Record<string, BehaviorSubject<ICategories>> = {};

  subscriptions: Subscription;

  constructor(
    private db: AngularFireDatabase
  ) {
  }

  testForData(): Observable<any> {
    return this.db.object(`annotations/${this.surveyId}`).valueChanges().pipe(take(1));
  }

  setupNewSurvey(surveyId: string) {
    this.subscriptions?.unsubscribe();
    this.subscriptions = new Subscription();
    this.surveyId = surveyId;
    this.subscriptions.add(this.db.object<IAllAnnotatedStatements>(`annotations/${surveyId}/annoByUser`).valueChanges()
      .pipe(switchMap(data => data ? of(data) : EMPTY))
      .subscribe((data) => this.annoByUser$.next(data)));
    this.subscriptions.add(this.db.object<IDictionnaries>(`annotations/${surveyId}/dictionary`).valueChanges()
      .pipe(switchMap(data => data ? of(data) : EMPTY))
      .subscribe((data) => this.dictionary$.next(data)));
  }

  getAllCategories(): Observable<Record<string, ICategories>> {
    return this.db.object<Record<string, ICategories>>(`annotations/${this.surveyId}/categories`).valueChanges();
  }

  setupCategoriesForQuestion(questionKey: string) {
    if (!this.categories$[questionKey]) {
      this.categories$[questionKey] ??= new BehaviorSubject<ICategories>({});
      this.subscriptions.add(this.db.object<ICategories>(`annotations/${this.surveyId}/categories/${questionKey}`).valueChanges()
        .pipe(switchMap(data => data ? of(data) : EMPTY))
        .subscribe((data) => this.categories$[questionKey].next(data)));
    }
    return this.categories$[questionKey];
  }

  setAnnotationByUserToFB(annoByUser: IAllAnnotatedStatements) {
    try {
      if (annoByUser satisfies IAllAnnotatedStatements) {
        this.annoByUser$.next(cloneDeep(annoByUser));
        return this.db.object(`annotations/${this.surveyId}/annoByUser`).set(removeUndefined(annoByUser));
      }
    } catch (e) {
      console.log('setAnnotationByUserToFB', e);
    }
  }

  setDictionaryToFB(dictionary: IDictionnaries) {
    try {
      if (dictionary satisfies IDictionnaries) {
        this.dictionary$. next(cloneDeep(dictionary));
        return this.db.object(`annotations/${this.surveyId}/dictionary`).set(removeUndefined(dictionary));
      }
    } catch (e) {
      console.log('setDictionaryToFB', e);
    }
  }

  deleteAllCategories(questionKey: string) {
    try {
      return this.db.object(`annotations/${this.surveyId}/categories/${questionKey}`).remove();
    } catch (e) {
      console.log('deleteAllCategories', e);
    }
  }

  setCategoriesToFB(categories: ICategories, questionKey: string) {
    try {
      if (categories satisfies ICategories) {
        this.db.object(`annotations/${this.surveyId}/categories/${questionKey}`).set(removeUndefined(categories)).then();
      }
    } catch (e) {
      console.log('setCategoriesToFB', e);
    }
  }

}
